import { PinType, UrlParams } from '@/types';

/**
 * Parse a reveal link. The decryption key travels after "#", which browsers never send to a server, so it
 * stays out of CloudFront's logs and Referer headers:
 *
 *   /secret/<secretIdPart1>/#/<secretIdPart2>/<randomWord>[/pin|/dpin]
 *
 * Links made before that change carry everything in the path and still open:
 *
 *   /secret/<secretIdPart1>/<secretIdPart2>/<randomWord>[/pin|/dpin]   (also without /secret)
 *
 * The segments of the path and of the fragment are read as one list, so both shapes parse the same way.
 */
export const parseRevealUrl = (pathname: string, hash = ''): UrlParams | null => {
    try {
        const segments = (value: string) => value.split('/').filter(Boolean);
        let parts = [...segments(pathname), ...segments(hash.replace(/^#/, ''))];

        // Handle /secret/ prefix - remove it if present
        if (parts.length > 0 && parts[0].toLowerCase() === 'secret') {
            parts = parts.slice(1);
        }

        if (parts.length < 3) {
            console.error('[parseRevealUrl] Invalid URL: insufficient path segments. Need 3, got:', parts.length);
            return null;
        }

        const secretIdPart1 = decodeURIComponent(parts[0]);
        const secretIdPart2 = decodeURIComponent(parts[1]);
        const randomWord = decodeURIComponent(parts[2]);
        const secretId = secretIdPart1 + secretIdPart2;

        let pinType: PinType | undefined;

        if (parts.length >= 4) {
            const pinPart = parts[3].toLowerCase();
            if (pinPart === 'pin') {
                pinType = 'static';
            } else if (pinPart === 'dpin') {
                pinType = 'dynamic';
            }
        }

        return {
            secretId,
            randomWord,
            ...(pinType ? { pinType } : {}),
            secretIdPart1,
            secretIdPart2,
        };
    } catch (error) {
        console.error('[parseRevealUrl] Error parsing URL_:', error);
        return null;
    }
};

/**
 * Validate URL parameters
 */
export const validateUrlParams = (params: UrlParams | null): boolean => {
    if (!params) return false;

    const { secretId, randomWord } = params;

    if (!secretId || secretId.length < 10) {
        console.error('Invalid secretId');
        return false;
    }

    if (!randomWord || randomWord.length < 10) {
        console.error('Invalid randomWord (decryption key)');
        return false;
    }

    return true;
};

/**
 * Build a reveal link in the current format, with the second half of the id, the key and the PIN flag after
 * "#" (see parseRevealUrl).
 */
export const buildRevealUrl = (
    baseUrl: string,
    secretIdPart1: string,
    secretIdPart2: string,
    randomWord: string,
    pinType?: PinType,
    includeSecretPrefix = true
): string => {
    const prefix = includeSecretPrefix ? '/secret' : '';
    let url = `${baseUrl}${prefix}/${encodeURIComponent(secretIdPart1)}/#/${encodeURIComponent(secretIdPart2)}/${encodeURIComponent(randomWord)}`;

    if (pinType === 'static') {
        url += '/pin';
    } else if (pinType === 'dynamic') {
        url += '/dpin';
    }

    return url;
};
